import { ref as dbRef, onValue } from 'firebase/database';
import { db } from '../firebase';
import { queuedBugReportCount } from './bugReports';

// Everything a bug report could conceivably need besides the raw game state.
// Matt, 2026-10-10: "make sure that we are always passing in any information
// that might or even remotely could be relevant to any bug report." The full
// `game` already rides along as gameState; this is the rest - which screen,
// who the reporter is AT THE TABLE, whose turn, the room, the connection, and
// whatever went wrong just before they tapped the bug.
//
// Rules for what goes in: compact (no giant blobs), no tokens, no other
// players' emails - names and seats only. And gathering must never be the
// reason a report fails to send, so every piece is fenced off on its own.

const MAX_ERRORS = 8;
const recentErrors = [];
let firebaseConnected = null;   // null = never heard from .info/connected
let connectionChangedAt = null;
let installed = false;

const clip = (value, n = 300) => String(value ?? '').slice(0, n);

export const noteError = (kind, message) => {
  recentErrors.push({ kind, message: clip(message), at: new Date().toISOString() });
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
};

export const recentErrorsForReport = () => recentErrors.slice();

/**
 * Called once from main.js: start keeping the last few uncaught errors and
 * the RTDB connection state, so a report can say what the app was going
 * through, not just where it ended up.
 */
export function installBugContextCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (event) => {
    try {
      const where = event.filename ? ` @ ${event.filename.split('/').pop()}:${event.lineno}` : '';
      noteError('error', `${event.message || event.error?.message || 'error'}${where}`);
    } catch { /* never let the recorder throw */ }
  });
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const r = event.reason;
      noteError('unhandledrejection', r?.message || r);
    } catch { /* ditto */ }
  });
  try {
    if (db) {
      onValue(dbRef(db, '.info/connected'), (snap) => {
        firebaseConnected = Boolean(snap.val());
        connectionChangedAt = new Date().toISOString();
      });
    }
  } catch { /* offline-only build: nothing to watch */ }
}

// Which screen App.vue is showing, derived the same way its v-if chain is.
const screenOf = (state, getters) => {
  if (!getters.isApproved) return 'siteGate';
  if (getters.isFinished) return 'scoreSheet';
  if (state.game) return 'gameTable';
  if (state.roomId) return 'onlineLobby';
  return 'menuOrLobby';
};

const openDialogs = () => {
  try {
    return [...document.querySelectorAll('[role="dialog"]')]
      .map((el) => el.getAttribute('aria-label') || el.className || 'dialog')
      .filter((label) => label !== 'Report a bug');
  } catch {
    return [];
  }
};

const gameSummary = (game, state, getters) => {
  if (!game) return null;
  const { meta = {}, turn = {}, players = [], columns = {} } = game;
  const active = players[meta.activePlayerIndex];
  const me = getters.me;
  const claimed = {};
  Object.entries(columns).forEach(([col, owner]) => {
    if (owner != null) claimed[col] = players.find((p) => p.id === owner)?.name || owner;
  });
  return {
    phase: meta.phase,
    turnNumber: meta.turn,
    seq: meta.seq,
    seed: meta.seed,
    winner: meta.winnerId ? (players.find((p) => p.id === meta.winnerId)?.name || meta.winnerId) : null,
    playerCount: players.length,
    // Names, seats and bot-ness only - online ids are uids, never emails.
    players: players.map((p) => ({
      seat: p.seat,
      name: p.name,
      isBot: p.isBot,
      claimed: Object.values(columns).filter((owner) => owner === p.id).length,
      ...(state.roomId ? { present: !p.isBot ? Boolean(state.presence?.[p.id]) : null } : {}),
    })),
    mySeat: me ? me.seat : null,
    myName: me ? me.name : null,
    activeSeat: active ? active.seat : null,
    activeName: active ? active.name : null,
    activeIsBot: active ? active.isBot : null,
    isMyTurn: Boolean(me && active && me.id === active.id),
    thinking: Boolean(state.thinkingPlayerId),
    turnPhase: turn.phase,
    dice: turn.dice,
    runners: turn.runners,
    optionCount: Array.isArray(turn.options) ? turn.options.length : null,
    moved: turn.moved,
    canRoll: getters.canRoll,
    canStop: getters.canStop,
    claimedColumns: claimed,
    absentSeats: (getters.absentPlayerIds || [])
      .map((id) => players.find((p) => p.id === id)?.seat),
    lastLog: Array.isArray(game.log) ? game.log.slice(-3) : null,
  };
};

const roomSummary = (state, getters) => {
  if (!state.roomId) return null;
  const meta = state.roomMeta || {};
  return {
    roomId: state.roomId,
    roomCode: meta.roomCode || null,
    status: meta.status || null,
    tableSize: meta.tableSize ?? null,
    async: Boolean(meta.async),
    iAmHost: getters.isHost,
    isMyOnlineTurn: getters.isMyOnlineTurn,
    connectedHumans: Object.keys(state.presence || {}).length,
    roomError: state.roomError ? clip(state.roomError?.message || state.roomError) : null,
  };
};

const section = (fn) => {
  try {
    return fn();
  } catch (e) {
    return { gatherError: clip(e?.message || e, 120) };
  }
};

/**
 * The context object attached to a report. `store` is the Vuex store. Never
 * throws: a section that fails reports its own failure instead.
 */
export function buildBugContext(store) {
  const state = store?.state || {};
  const getters = store?.getters || {};
  const context = {
    screen: section(() => screenOf(state, getters)),
    openDialogs: section(openDialogs),
    online: Boolean(state.roomId),
    roomCode: state.roomMeta?.roomCode || null,
    uid: state.user?.uid || null,
    game: section(() => gameSummary(state.game, state, getters)),
    room: section(() => roomSummary(state, getters)),
    settings: section(() => ({
      playerName: state.settings?.playerName ?? null,
      opponents: state.settings?.opponents ?? null,
      hasSavedGame: Boolean(state.hasSavedGame),
    })),
    auth: section(() => ({
      firebaseConfigured: Boolean(state.firebaseConfigured),
      authReady: Boolean(state.authReady),
      signedIn: Boolean(state.user),
      isAnonymous: Boolean(state.user?.isAnonymous),
      siteStatus: state.siteUser?.status || null,
      isAdmin: Boolean(state.siteUser?.isAdmin),
    })),
    network: section(() => ({
      navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : null,
      firebaseConnected,
      connectionChangedAt,
      effectiveType: navigator?.connection?.effectiveType || null,
      queuedBugReports: queuedBugReportCount(),
    })),
    recentErrors: section(recentErrorsForReport),
    app: section(() => ({
      buildTime: process.env.VUE_APP_BUILD_TIME || null,
      standalone: Boolean(window.matchMedia?.('(display-mode: standalone)')?.matches
        || window.navigator?.standalone),
      serviceWorkerControlled: Boolean(navigator?.serviceWorker?.controller),
      visibility: document.visibilityState || null,
      language: navigator?.language || null,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      pageAgeSeconds: Math.round((performance?.now?.() || 0) / 1000),
    })),
  };
  // RTDB's set() rejects `undefined` anywhere in the tree; a JSON round trip
  // drops those keys so one missing field can't sink the whole report.
  return JSON.parse(JSON.stringify(context));
}
